import React, { useEffect, useLayoutEffect, useMemo } from 'react';
import { Group, Token, Tooltip, Typography, Wrapper } from '@screentone/core';
import classnames from 'classnames';
import { isEqual } from 'lodash';
import { v4 as uuidv4 } from 'uuid';

import { CommonEntityProps } from 'components/datamodel/commonEntityProps';
import { ResponsiveLoader } from 'components/responsive-loader/ResponsiveLoader';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useDataModelContext } from 'contexts/datamodel/useDataModel';
import { ArticleItem, NewswiresListContainer, useCollectionDtoByIdQuery } from 'data/generated/graphql';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { useSelectedCollections } from 'features/multicollection-edit/contexts/SelectedCollectionsContext';
import { reportError } from 'utils/errorBoundary';
// eslint-disable-next-line import/no-cycle -- TODO: pre-existing cycle via DataModelContext, fix when context is restructured
import { ManualItems } from './components/ManualItems/ManualItems';
import styles from './NewswiresList.module.scss';
import { formatNumItemsText, getTokenState, WARNING_THRESHOLD } from '../../List.utils';

import type { EntityComponent } from 'contexts/datamodel/DataModelContext.core';
import { isArticleItem } from 'contexts/datamodel/DataModelContext.utils';

interface NewswiresListProps extends CommonEntityProps {
  data: NewswiresListContainer;
}

const populateUUIDs = (entity: EntityComponent, initArticles: ArticleItem[]) => {
  const list = entity as NewswiresListContainer;
  list.collection = initArticles.map((article) => ({ ...article, uuid: article.uuid || uuidv4() }));
};

export const NewswiresList = React.memo(
  ({ data: newswiresList, parentHierarchyId, index }: NewswiresListProps) => {
    const { generateHierarchyId, modifyEntity } = useDataModelContext();
    const hierarchyId = generateHierarchyId(newswiresList, parentHierarchyId, index);
    const { syncedCollectionsRef, activeTabId, setErroredCollection } = useSelectedCollections();
    const isActive = activeTabId === newswiresList.uuid;

    const {
      data: collectionDTO,
      error: collectionDTOError,
      isLoading: isCollectionDTOLoading
    } = useCollectionDtoByIdQuery(
      { id: newswiresList.attributes.allessehId, publicationKey: newswiresList.attributes.publicationKey },
      {
        enabled: !!newswiresList.attributes.allessehId && !!newswiresList.attributes.publicationKey,
        staleTime: Infinity, // Don't refetch collection once already loaded
        retry: 3
      }
    );

    useEffect(() => {
      if (collectionDTOError) {
        if (collectionDTOError instanceof Error) {
          reportError(collectionDTOError.message);
        } else {
          reportError(
            `An unknown error occurred while attempting to load the collection with id ${newswiresList.attributes.allessehId}.`
          );
        }
        setErroredCollection(newswiresList.uuid, true);
      } else {
        setErroredCollection(newswiresList.uuid, false);
      }
    }, [collectionDTOError, newswiresList.attributes.allessehId, newswiresList.uuid, setErroredCollection]);

    // Map items from the fetched collection to ArticleItems
    const initArticles = useMemo(
      () =>
        collectionDTO?.collectionDTOById.root.collection
          // NOTE: Remove this filter if we want to support LinkedItems in Newswires collections, but for now we only want to support ArticleItems
          .filter(isArticleItem),
      [collectionDTO?.collectionDTOById.root.collection]
    );

    // Sync fetched articles into the data model before paint to avoid a flash of empty list.
    // syncedCollectionsRef persists across tab-switch unmount/remount cycles and prevents
    // re-populating a collection the user has intentionally emptied.
    useLayoutEffect(() => {
      if (!initArticles || syncedCollectionsRef.current[newswiresList.uuid]) return;
      syncedCollectionsRef.current[newswiresList.uuid] = true;
      modifyEntity(hierarchyId, (entity) => populateUUIDs(entity, initArticles));
    }, [initArticles, hierarchyId, modifyEntity, newswiresList.uuid, syncedCollectionsRef]);

    const totalItemsCount = newswiresList.collection
      .filter(isArticleItem)
      .filter((item) => !('status' in item.attributes && item.attributes.status === 'embargo')).length;
    const { color: tokenColor, icon: tokenIcon, tooltipText: tokenTooltipText } = getTokenState(totalItemsCount);
    const numItemsText = formatNumItemsText(totalItemsCount);

    return (
      <Wrapper
        className={styles.wrapper}
        padding={{ all: 'mlg' }}
        margin={{ top: 'mlg' }}
        data-hierarchy-id={hierarchyId}
        data-model-uuid={newswiresList.uuid}
      >
        <Group align="start" valign="baseline" margin={{ bottom: 'sm' }} gap="sm">
          <Typography variant="label2">{newswiresList.attributes.name}</Typography>
          <Tooltip
            usePortal
            className={classnames(
              styles.numItemsTooltip,
              totalItemsCount >= WARNING_THRESHOLD ? '' : styles.numItemsTooltipHidden
            )}
          >
            {totalItemsCount >= WARNING_THRESHOLD && (
              <Tooltip.Content position="top">{tokenTooltipText}</Tooltip.Content>
            )}
            <Tooltip.Trigger>
              <Token color={tokenColor} icon={tokenIcon}>
                {numItemsText}
              </Token>
            </Tooltip.Trigger>
          </Tooltip>
        </Group>
        {isCollectionDTOLoading ? (
          <ResponsiveLoader size="lg" />
        ) : collectionDTOError ? (
          <Typography variant="note" color="lava">
            Failed to load collection:{' '}
            {collectionDTOError instanceof Error ? collectionDTOError.message : 'Unknown error'}
          </Typography>
        ) : (
          <ManualItems
            metadata={collectionDTO?.collectionDTOById.metadata}
            articles={newswiresList.collection.filter(isArticleItem)}
            listHierarchyId={hierarchyId}
            isActive={isActive}
          />
        )}
      </Wrapper>
    );
  },
  (prevProps, nextProps) =>
    isEqual(prevProps.data, nextProps.data) &&
    prevProps.parentHierarchyId === nextProps.parentHierarchyId &&
    prevProps.index === nextProps.index
);

NewswiresList.displayName = 'NewswiresList';
